import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchHeadlessIntegrations,
  fetchHeadlessUsers,
  createHeadlessIntegration,
  updateHeadlessIntegration,
  regenerateHeadlessSecret,
  fetchHeadlessAudit,
} from '../utils/api';
import '../styles/HeadlessReviewPage.css';
import '../styles/HeadlessIntegrationsPage.css';

function HeadlessIntegrationsPage() {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [name, setName] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [defaultSuffix, setDefaultSuffix] = useState('');
  const [newSecret, setNewSecret] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const [ints, usersResp] = await Promise.all([
        fetchHeadlessIntegrations(),
        fetchHeadlessUsers(),
      ]);
      setIntegrations(ints);
      setUsers(usersResp);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load integrations' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && (user.accountType === 'admin' || user.accountType === 'superadmin')) {
      load();
    }
  }, [user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setMessage({ type: '', text: '' });
      const result = await createHeadlessIntegration({
        name: name.trim() || 'Headless integration',
        owner_user_id: ownerUserId,
        default_suffix: defaultSuffix.trim() || null,
      });
      setNewSecret({
        client_id: result.integration?.client_id,
        client_secret: result.client_secret,
      });
      setName('');
      setDefaultSuffix('');
      await load();
      setMessage({ type: 'success', text: 'Integration created. Copy the client secret now — it will not be shown again.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Create failed' });
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this integration? All refresh tokens will be invalidated.')) return;
    try {
      await updateHeadlessIntegration(id, { revoke: true });
      await load();
      setMessage({ type: 'success', text: 'Integration revoked' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Revoke failed' });
    }
  };

  const handleRegenerate = async (id) => {
    if (!window.confirm('Regenerate client secret? Existing refresh tokens will be invalidated.')) return;
    try {
      const result = await regenerateHeadlessSecret(id);
      setNewSecret({
        client_id: result.integration?.client_id,
        client_secret: result.client_secret,
      });
      setMessage({ type: 'success', text: 'New secret generated. Copy it now.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Regenerate failed' });
    }
  };

  const handleToggleEntryConfirm = async (id, requireConfirm) => {
    try {
      setMessage({ type: '', text: '' });
      await updateHeadlessIntegration(id, { require_entry_confirmation: requireConfirm });
      await load();
      setMessage({
        type: 'success',
        text: requireConfirm
          ? 'Entry confirmation enabled (Cascade-style wait; Headless has no confirm UI yet).'
          : 'Entry confirmation off — entry create/update apply immediately on Headless chat.',
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Update failed' });
    }
  };

  const handleSelect = async (id) => {
    setSelectedId(id);
    try {
      const events = await fetchHeadlessAudit(id);
      setAudit(events);
    } catch (_) {
      setAudit([]);
    }
  };

  if (!user || (user.accountType !== 'admin' && user.accountType !== 'superadmin')) {
    return (
      <div className="headless-integrations-page">
        <div className="alert alert-error">You do not have permission to manage Headless integrations.</div>
      </div>
    );
  }

  return (
    <div className="headless-integrations-page">
      <div className="headless-page-header">
        <h1>Headless Integrations</h1>
        <p>Mint client credentials for external servers. Integrations act as the assigned owner user on a specific horizon (tools, data, and auto-projects).</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>{message.text}</div>
      )}

      {newSecret && (
        <div className="headless-secret-banner">
          <h3>One-time credentials</h3>
          <p><strong>client_id:</strong> <code>{newSecret.client_id}</code></p>
          <p><strong>client_secret:</strong> <code>{newSecret.client_secret}</code></p>
          <button type="button" onClick={() => setNewSecret(null)}>Dismiss</button>
        </div>
      )}

      <form className="headless-create-form" onSubmit={handleCreate}>
        <h2>Create integration</h2>
        <div className="headless-form-row">
          <label>
            Name
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="My external server" />
          </label>
          <label>
            Owner user
            <select value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} required>
              <option value="">Select user…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.email} ({u.user_type || u.accountType || 'user'})</option>
              ))}
            </select>
          </label>
          <label>
            Default horizon
            <input type="text" value={defaultSuffix} onChange={(e) => setDefaultSuffix(e.target.value)} placeholder="e.g. rsmg_sales" />
            <span className="headless-field-hint">
              Suffix where auto-projects, tools, and data live. Blank uses the owner user’s active horizon.
            </span>
          </label>
        </div>
        <button type="submit" className="btn btn-primary">Create integration</button>
      </form>

      {loading ? (
        <div className="headless-loading">Loading…</div>
      ) : (
        <table className="headless-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Client ID</th>
              <th>Owner</th>
              <th>Horizon</th>
              <th>Entry confirm</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {integrations.map((intg) => {
              const owner = users.find((u) => u.id === intg.owner_user_id);
              const requireConfirm = intg.require_entry_confirmation === true;
              return (
                <tr key={intg.id} className={selectedId === intg.id ? 'selected' : ''}>
                  <td>{intg.name}</td>
                  <td><code>{intg.client_id}</code></td>
                  <td>{owner?.email || intg.owner_user_id}</td>
                  <td><code>{intg.default_suffix || 'owner active'}</code></td>
                  <td>
                    <label className="headless-toggle-label" title="When off, entry_create / entry_update apply immediately on Headless chat (default). When on, Cascade confirm wait — not usable from Headless UI yet.">
                      <input
                        type="checkbox"
                        checked={requireConfirm}
                        disabled={!!intg.revoked_at}
                        onChange={(e) => handleToggleEntryConfirm(intg.id, e.target.checked)}
                      />
                      <span>{requireConfirm ? 'On' : 'Off'}</span>
                    </label>
                  </td>
                  <td>{intg.revoked_at ? <span className="headless-revoked">Revoked</span> : 'Active'}</td>
                  <td className="headless-actions">
                    <button type="button" onClick={() => handleSelect(intg.id)}>Audit</button>
                    {!intg.revoked_at && (
                      <>
                        <button type="button" onClick={() => handleRegenerate(intg.id)}>Regenerate secret</button>
                        <button type="button" className="danger" onClick={() => handleRevoke(intg.id)}>Revoke</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!integrations.length && (
              <tr><td colSpan={7} className="headless-empty">No integrations yet</td></tr>
            )}
          </tbody>
        </table>
      )}

      {selectedId && audit.length > 0 && (
        <div className="headless-audit-panel">
          <h3>Recent audit events</h3>
          <ul>
            {audit.map((ev) => (
              <li key={ev.id}>
                <strong>{ev.event_type}</strong>
                <span>{new Date(ev.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default HeadlessIntegrationsPage;
